import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, inject, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { initialConfig, MaskPipe, NgxMaskModule } from 'ngx-mask';
import { FormFieldModule } from '../../wrapper/form-field/form-field.component';
import { CyTestDirective } from '@portal/directive';
import { PortalFieldTypeConfig } from '@portal/form/data-access';
import { EMPTY_VALUE } from '@core/data-access';

@Component({
  selector: 'portal-form-input',
  standalone: true,
  imports: [NgIf, FormlyModule, ReactiveFormsModule, NgxMaskModule, CyTestDirective],
  providers: [MaskPipe],
  templateUrl: './input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent extends FieldType<PortalFieldTypeConfig> {
  protected readonly maskPipe = inject(MaskPipe);
  protected readonly defaultValue = EMPTY_VALUE;
  override defaultOptions = { props: { maskConfig: { mask: '', config: initialConfig } } };

  @HostBinding('class') get hostClass(): string {
    return this.disabled ? 'field-value' : 'field-input-wrapper';
  }

  get maskConfig() {
    return this.props.maskConfig ?? this.defaultOptions.props.maskConfig;
  }

  /**
   * Check if form or field is disabled
   * @returns boolean
   * @readonly
   */
  get disabled(): boolean {
    return !!this.options?.formState?.disabled || !!this.props?.disabled;
  }

  get type(): string {
    return this.disabled ? 'hidden' : this.props.type || 'text';
  }

  get display(): string {
    const { mask, config } = this.maskConfig;
    if (mask) {
      return this.maskPipe.transform(this.formControl.value, config?.patterns ? [mask, config.patterns] : mask) || this.defaultValue;
    }
    return this.props.displayWith?.(this.formControl.value) || this.formControl.value || this.defaultValue;
  }
}

@NgModule({
  imports: [
    FormFieldModule,
    FormlyModule.forChild({
      types: [
        { name: 'input', component: InputComponent, wrappers: ['form-field'] },
        { name: 'string', extends: 'input' },
        { name: 'password', extends: 'input', defaultOptions: { props: { type: 'password' } } },
        { name: 'number', extends: 'input', defaultOptions: { props: { type: 'number' } } },
        { name: 'integer', extends: 'input', defaultOptions: { props: { type: 'number' } } },
      ],
    }),
  ],
})
export class FormInputModule {}
